import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import Notification from '@rio-cloud/rio-uikit/Notification';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { type FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ApiWriteServiceOrder } from '#api/model/types.ts';
import { useCreateOrderMutation, useUpdateOrderMutation } from '#api-bridge/orderApi.ts';
import { ControlSize } from '#components/form/ControlSize.ts';
import { ControlledFormInput } from '#components/form/FormInput.tsx';
import { ControlledSelect } from '#components/form/FormSelect.tsx';
import { reportErrorToSentry } from '#configuration/setup/sentry.ts';
import { type ServiceOrder, serviceOrderTypes } from '#domain/ServiceOrder.ts';
import { parseGeoCoordinateString } from '#utils/geoParsing.util.ts';

import { LocationFormView } from './LocationForm.tsx';
import type { OrderDialogState } from './OrderDialog.tsx';
import { type ServiceOrderFormData, ServiceOrderFormSchema } from './ServiceOrderFormValidation.ts';
import { getServiceOrderFormDefaultValues } from './serviceOrderFormDefaults.ts';
import { composeDateTimeValue } from './timeWindowFields.ts';

type Props = {
    state: OrderDialogState;
    order?: ServiceOrder;
    onClose: () => void;
    setActiveOrder: (orderId: string) => void;
};

export const ServiceOrderForm = ({ state, order, onClose, setActiveOrder }: Props) => {
    const intl = useIntl();
    const formContext = useForm<ServiceOrderFormData>({
        resolver: zodResolver(ServiceOrderFormSchema),
        defaultValues: getServiceOrderFormDefaultValues(state, order),
        mode: 'onTouched',
    });
    const { control, handleSubmit } = formContext;
    const [createOrder, createState] = useCreateOrderMutation();
    const [updateOrder, updateState] = useUpdateOrderMutation();

    const onValidationErrors = (errors: FieldErrors<ServiceOrderFormData>) => {
        console.error('[feature/form-validation] service order validation errors:', errors);
        Notification.error(
            intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.error',
                defaultMessage: 'The order could not be saved. Please correct the form and try again.',
            })
        );
    };

    const serviceTypeOptions: SelectOption[] = [
        ...serviceOrderTypes.map(type => ({
            id: type,
            label: intl.formatMessage({
                id: `intl-msg:ordercom-cloud-frontend.service.type.${type}`,
            }),
        })),
    ];

    const submit = async (data: ServiceOrderFormData) => {
        const stop = data.executionStop;
        const payload: ApiWriteServiceOrder = {
            type: 'service',
            order: data.description.trim(),
            category: data.serviceType,
            company: data.company?.trim() || undefined,
            note: data.note?.trim() || undefined,
            execution_stop: {
                name: data.serviceType,
                type: 'execute',
                location: {
                    street: stop.location.street.trim(),
                    city: stop.location.city.trim(),
                    zip_code: stop.location.zipCode.trim(),
                    country: stop.location.country,
                },
                start_target: composeDateTimeValue(stop.startDate, stop.startTime),
                end_target: composeDateTimeValue(stop.endDate, stop.endTime),
                coordinates:
                    stop.latitude !== undefined && stop.longitude !== undefined
                        ? {
                              latitude: parseGeoCoordinateString(stop.latitude),
                              longitude: parseGeoCoordinateString(stop.longitude),
                          }
                        : undefined,
            },
        };

        const isEditing = state === 'editOrder' && order;
        try {
            const result = isEditing
                ? await updateOrder({ id: order.id, order: payload }).unwrap()
                : await createOrder(payload).unwrap();
            setActiveOrder(result.id);
            Notification.success(
                intl.formatMessage({
                    id: isEditing
                        ? 'intl-msg:ordercom-cloud-frontend.orders.edit-dialog.success'
                        : 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.success',
                    defaultMessage: isEditing ? 'Order successfully changed!' : 'Order successfully created!',
                })
            );
            onClose();
        } catch (submissionError) {
            reportErrorToSentry(submissionError);
            Notification.error(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.error',
                    defaultMessage: 'The order could not be saved. Please correct the form and try again.',
                })
            );
        }
    };

    return (
        <FormProvider {...formContext}>
            <form onSubmit={handleSubmit(submit, onValidationErrors)} data-testid='service-order-form'>
                <div className='display-grid grid-cols-2-max-content-1fr-sm column-gap-20-sm padding-x-20 padding-top-20 align-items-end'>
                    <div className='width-100pct width-200-sm'>
                        <ControlledSelect
                            name='serviceType'
                            control={control}
                            label={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.service.type',
                                defaultMessage: 'Service type',
                            })}
                            options={serviceTypeOptions}
                            formGroupClassName='form-group-lg'
                        />
                    </div>
                    <ControlledFormInput
                        name='description'
                        control={control}
                        size={ControlSize.LARGE}
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.service.description',
                            defaultMessage: 'Description',
                        })}
                    />
                </div>
                <div className='padding-x-20'>
                    <div className='nav nav-pills'>
                        <ul className='nav nav-pills'>
                            <li className='active' data-testid='execution-stop-tab'>
                                <div className='display-flex align-items-center gap-5'>
                                    {intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.stop.type.execute',
                                        defaultMessage: 'Execute',
                                    })}
                                    <span className='uikit-icon icon-wrench' />
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className='padding-x-20 padding-y-20'>
                    <LocationFormView
                        addressType='executionStop'
                        commentName='note'
                        companyNameField='company'
                        coordinatesPrefix='executionStop'
                        prefix='executionStop'
                        showCommentInitially={Boolean(order?.note)}
                        addressDisabled={true}
                    />
                </div>
                <div className='display-flex justify-content-end gap-10 padding-x-20 padding-bottom-20'>
                    <Button type='button' bsStyle={Button.DEFAULT} onClick={onClose}>
                        {intl.formatMessage({ id: 'intl-msg:common-message.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button
                        type='submit'
                        bsStyle={Button.PRIMARY}
                        disabled={createState.isLoading || updateState.isLoading}
                    >
                        {intl.formatMessage({ id: 'intl-msg:common-message.save', defaultMessage: 'Save' })}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};
